/**
 * Gestione dei documenti di un obbligo: caricamento, cartelle, spostamenti.
 *
 * E' la via di caricamento del back-office: il Comune ha dei file e li mette
 * online. Il percorso e' corto di proposito — si sceglie l'obbligo, si rilascia
 * il file, si scrive il titolo del link pubblico — ma cio' che **non** salta e'
 * che il file finisce in una bozza e passa dalla validazione del contenuto: un
 * PDF da scansione e' respinto qui come altrove.
 *
 * Il caricamento **non pubblica**: sono due gesti con conseguenze diverse, uno
 * reversibile e l'altro no. Il secondo sta nel riquadro «Da pubblicare», che
 * monta lo stesso blocco dell'alberatura (`components/ContenutiNodo`) e
 * dichiara gli effetti — versione immutabile, alias «-latest», at_map
 * rigenerata — prima della conferma.
 *
 * Il comando sta anche qui, e non solo nell'alberatura, perche' quella pagina e'
 * riservata al super admin mentre l'API consente al redattore di pubblicare
 * dentro il proprio perimetro: chi carica deve poter concludere il lavoro senza
 * chiedere a qualcun altro di premere l'ultimo pulsante.
 *
 * Le cartelle sono un raggruppamento di presentazione: non hanno un indirizzo
 * pubblico, non entrano nella at_map e non compaiono nell'URL dei documenti che
 * contengono. E' la ragione per cui spostare un file e' innocuo anche quando il
 * file e' gia' pubblicato — e il motivo per cui questa pagina puo' offrire lo
 * spostamento senza avvertenze.
 *
 * Ogni documento ha una **durata di pubblicazione**: al caricamento vale quella
 * dell'obbligo (cinque anni, art. 8 c. 3, se il Comune non ne ha dichiarata
 * un'altra) e la si puo' cambiare, anche dopo, con «Durata…». Trascorsa, il
 * documento passa nell'archivio della pagina — lo fa il worker, o «Archivia» —
 * e qui compare sotto «Archiviati»: resta accessibile, non si cancella.
 *
 * Lo stesso file si puo' pubblicare **in piu' obblighi** caricandolo una volta
 * sola («Pubblica anche in…»): ogni obbligo riceve una copia sua, con nome,
 * indirizzo e durata di quell'obbligo, e le copie restano legate — la tabella
 * dice «Pubblicato anche in…», e «Pubblica in tutti gli obblighi» le mette
 * online insieme.
 *
 * Trascinamento **e** comando esplicito: «Sposta in…» apre una modale con
 * l'elenco delle cartelle ed e' la via accessibile da tastiera. Non e' un
 * ripiego per browser vecchi: il trascinamento non e' utilizzabile con uno
 * screen reader, e la verifica manuale da tastiera e' obbligatoria e non
 * sostituibile.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { DragEvent, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ErroreApi,
  aggiornaCartella,
  archiviaDocumento,
  caricaDocumentoSuNodo,
  creaCartella,
  elencaPubblicazioni,
  eliminaCartella,
  leggiCartelle,
  leggiPubblicazioniNodo,
  pubblica,
  spostaDocumento,
} from "../api/client";
import type {
  Cartella,
  Documento,
  Nodo,
  Pubblicazione,
  RispostaPubblicazioniNodo,
} from "../api/types";
import { AlberoNodi } from "../components/AlberoNodi";
import { CampoDurata } from "../components/CampoDurata";
import { ContenutiNodo } from "../components/ContenutiNodo";
import { EsitoAccessibilita } from "../components/EsitoAccessibilita";
import { LegendaAccessibilita } from "../components/LegendaAccessibilita";
import { Modale } from "../components/Modale";
import { ModaleDurata } from "../components/ModaleDurata";
import { ModaleNotaAssenza } from "../components/ModaleNotaAssenza";
import { ModaleScadenza } from "../components/ModaleScadenza";
import { ModalePubblicaInTutti } from "../components/ModalePubblicaInTutti";
import { SceltaObblighi } from "../components/SceltaObblighi";
import { FileScelto, ZonaRilascio } from "../components/ZonaRilascio";
import { CampoDato, CampoLibero } from "../components/campi";
import {
  Avviso,
  Bottone,
  Chip,
  ChipAccessibilita,
  ChipStato,
  InCaricamento,
  Scheda,
  StatoVuoto,
} from "../components/primitive";
import { useAutenticazione } from "../auth/contesto";
import { useAlberatura } from "../dati/contesto-alberatura";
import {
  appiattisciCartelle,
  destinazioniAmmesse,
  percorsoCartella,
  trovaCartella,
} from "../lib/cartelle";
import { catenePerId, percorsoNodo } from "../lib/alberatura";
import {
  copieDi,
  copiePerIndirizzo,
  corpoCopie,
  daPubblicareInTutti,
  obblighiCandidati,
  testoCopie,
} from "../lib/copie";
import type { SceltaCopia, VoceDaPubblicare } from "../lib/copie";
import { inAttesaDiPubblicazione } from "../lib/documenti";
import {
  DURATA_MASSIMA_AL_CARICAMENTO,
  durataRisolta,
  leggiDurata,
  leggiScadenzaFile,
  nodiFoglia,
  regolePerNodo,
  testoDurata,
} from "../lib/scadenze";
import type { SceltaDurata } from "../lib/scadenze";
import { useRegole } from "../lib/useRegole";
import { useRisorsa } from "../lib/useRisorsa";
import { IntestazionePagina } from "../layout/Guscio";

/** Documento con la pubblicazione che lo espone, per la tabella. */
interface RigaDocumento {
  documento: Documento;
  pubblicazione: string;
  stato: "bozza" | "pubblicato" | "archiviato";
}

/** Corrente: ne' archiviato da solo, ne' parte di una pubblicazione archiviata. */
function corrente(riga: RigaDocumento): boolean {
  return riga.stato !== "archiviato" && !riga.documento.archiviato_il;
}

export function GestioneDocumenti() {
  const {
    ente,
    nodi,
    inCaricamento: alberaturaInCaricamento,
    ricarica: ricaricaAlberatura,
  } = useAlberatura();
  const { eSuperAdmin, nodiAssegnati } = useAutenticazione();
  const enteId = ente?.id ?? null;

  const [nodoScelto, setNodoScelto] = useState<Nodo | null>(null);
  const [cartellaCorrente, setCartellaCorrente] = useState<number | null>(null);
  const [erroreOperazione, setErroreOperazione] = useState<string | null>(null);
  const [esito, setEsito] = useState<ReactNode>(null);
  const [inLavorazione, setInLavorazione] = useState(false);

  const [fileScelto, setFileScelto] = useState<File | null>(null);
  const [titoloDocumento, setTitoloDocumento] = useState("");
  const [sceltaDurata, setSceltaDurata] = useState<SceltaDurata>("");
  const [finoAl, setFinoAl] = useState("");
  /** Scadenza del file al caricamento, gg/mm/aaaa; vuota = nessuna. */
  const [validoFinoAl, setValidoFinoAl] = useState("");
  const [durataDi, setDurataDi] = useState<Documento | null>(null);
  const [scadenzaDi, setScadenzaDi] = useState<Documento | null>(null);
  const [commentoAperto, setCommentoAperto] = useState(false);
  const [copieScelte, setCopieScelte] = useState<SceltaCopia[]>([]);
  const [daPubblicareTutti, setDaPubblicareTutti] = useState<VoceDaPubblicare[] | null>(null);
  const [daArchiviare, setDaArchiviare] = useState<Documento | null>(null);

  /** Pubblicazione in corso di pubblicazione: l'attesa si accende su quel pulsante. */
  const [inPubblicazione, setInPubblicazione] = useState<number | null>(null);

  const [nuovaCartella, setNuovaCartella] = useState<string | null>(null);
  const [daSpostare, setDaSpostare] = useState<Documento | null>(null);
  const [cartellaDaEliminare, setCartellaDaEliminare] = useState<Cartella | null>(null);

  useEffect(() => {
    document.title = "Documenti · Amministrazione trasparente";
  }, []);

  /** Obblighi applicabili dentro il perimetro di chi opera: foglie, non rami. */
  const selezionabile = useCallback(
    (nodo: Nodo) => {
      if (!nodo.applicabile) return false;
      const foglia = (nodo.figli ?? []).length === 0;
      if (!foglia) return false;
      return eSuperAdmin || nodiAssegnati.includes(nodo.id_nodo);
    },
    [eSuperAdmin, nodiAssegnati],
  );

  const foglieDisponibili = useMemo(
    () => nodiFoglia(nodi).filter(selezionabile),
    [nodi, selezionabile],
  );

  /**
   * `?nodo=<id_nodo>` apre la pagina gia' sull'obbligo.
   *
   * Ci arrivano i rimandi delle altre pagine — Scadenze e allerte,
   * Accessibilita' — che sanno gia' su quale obbligo si deve lavorare. Senza,
   * «Carica» portava su una pagina vuota e toccava cercare di nuovo l'obbligo
   * nell'albero. Si applica una volta sola, quando l'alberatura e' arrivata, e
   * solo a un obbligo che questa pagina offrirebbe comunque: il parametro non
   * allarga il perimetro, sceglie dentro di esso.
   */
  const [parametri, setParametri] = useSearchParams();
  const nodoRichiesto = parametri.get("nodo");
  const [richiestaApplicata, setRichiestaApplicata] = useState<string | null>(null);
  if (nodoRichiesto && nodoRichiesto !== richiestaApplicata && foglieDisponibili.length > 0) {
    setRichiestaApplicata(nodoRichiesto);
    const trovato = foglieDisponibili.find((nodo) => nodo.id_nodo === nodoRichiesto);
    if (trovato) {
      setNodoScelto(trovato);
      setCartellaCorrente(null);
      setCopieScelte([]);
    }
  }

  const idNodo = nodoScelto?.id_nodo ?? null;

  // Il percorso completo del nodo scelto: entra nell'etichetta del comando di
  // pubblicazione, e fra i 219 nodi cinquantadue denominazioni si ripetono.
  const catene = useMemo(() => catenePerId(nodi), [nodi]);

  const cartelle = useRisorsa<Cartella[]>(
    (segnale) => (idNodo ? leggiCartelle(idNodo, enteId, segnale) : Promise.resolve([])),
    [idNodo, enteId],
  );

  // Con l'archivio: i documenti usciti dalla pubblicazione corrente restano
  // dell'obbligo, e il redattore deve poterli ritrovare. Si separano qui sotto.
  const contenuti = useRisorsa<RispostaPubblicazioniNodo | null>(
    (segnale) =>
      idNodo
        ? leggiPubblicazioniNodo(idNodo, undefined, segnale, enteId, true)
        : Promise.resolve(null),
    [idNodo, enteId],
  );

  // La durata predefinita dell'obbligo: il modulo di caricamento la mostra per
  // esteso, «Come da regola dell'obbligo: 3 anni».
  const regole = useRegole(enteId);
  const regolaDi = useMemo(() => regolePerNodo(regole.dato ?? []), [regole.dato]);
  const durataObbligo = idNodo ? regolaDi.get(idNodo)?.durata_anni ?? null : null;
  // Finche' le regole non ci sono il modulo non promette «5 anni»: l'obbligo
  // potrebbe averne dichiarati tre.
  const regoleNote = regole.dato !== null && !regole.errore;

  /** Gli altri obblighi che possono ricevere una copia del file. */
  const candidatiCopie = useMemo(
    () => obblighiCandidati(nodi, selezionabile, idNodo),
    [nodi, selezionabile, idNodo],
  );
  const percorsoDi = useCallback((id: string) => percorsoNodo(catene, id), [catene]);

  /**
   * Contenuti dell'obbligo nella vista **del redattore**.
   *
   * La risorsa qui sopra e' la vista pubblica: espone i documenti dell'ultima
   * versione pubblicata, ed e' giusto che resti tale — la tabella serve a
   * ragionare su cio' che il cittadino vede. Ma le bozze non ci sono, e senza
   * bozze non si puo' pubblicare: chi carica un file resterebbe senza il gesto
   * successivo. Questa seconda lettura porta cio' che e' caricato e non ancora
   * pubblico, ed e' anche l'unica che dichiara `versione_di_lavoro_aperta`.
   */
  const daPubblicare = useRisorsa<Pubblicazione[]>(
    (segnale) =>
      idNodo
        ? elencaPubblicazioni({ nodo: idNodo, ente: enteId ?? undefined }, segnale)
        : Promise.resolve([]),
    [idNodo, enteId],
  );

  // Memoizzato: `?? []` produce un array nuovo a ogni render, e i `useMemo` che
  // lo prendono come dipendenza si ricalcolerebbero sempre — insieme
  // all'appiattimento ricorsivo dell'albero delle cartelle.
  const alberoCartelle = useMemo(() => cartelle.dato ?? [], [cartelle.dato]);

  /** Cio' che su questo obbligo aspetta di essere pubblicato. */
  const inAttesa = useMemo(
    () => (daPubblicare.dato ?? []).filter(inAttesaDiPubblicazione),
    [daPubblicare.dato],
  );

  /**
   * Documenti visibili nella cartella corrente.
   *
   * L'elenco arriva dalla vista **pubblica** del nodo, che espone i documenti
   * dell'ultima versione pubblicata: e' cio' che il cittadino vede, ed e' su
   * quello che il redattore ragiona quando riorganizza. I documenti ancora in
   * bozza compaiono dopo la pubblicazione, come sul portale.
   */
  const tutteLeRighe: RigaDocumento[] = useMemo(() => {
    const pubblicazioni = contenuti.dato?.pubblicazioni ?? [];
    return pubblicazioni.flatMap((pubblicazione) =>
      (pubblicazione.documenti ?? []).map((documento) => ({
        documento,
        pubblicazione: pubblicazione.titolo,
        stato: pubblicazione.stato,
      })),
    );
  }, [contenuti.dato]);

  const righe = useMemo(
    () =>
      tutteLeRighe.filter(
        (riga) =>
          corrente(riga) && (riga.documento.cartella_id ?? null) === cartellaCorrente,
      ),
    [tutteLeRighe, cartellaCorrente],
  );

  /**
   * L'archivio dell'obbligo, senza cartelle: e' un elenco di consultazione, come
   * sul portale, e una ripartizione pensata per la pagina corrente lo
   * spezzerebbe in gruppi quasi vuoti.
   */
  const archiviati = useMemo(
    () => tutteLeRighe.filter((riga) => !corrente(riga)),
    [tutteLeRighe],
  );

  const sottocartelle = useMemo(() => {
    if (cartellaCorrente == null) return appiattisciCartelle(alberoCartelle).filter(
      (riga) => riga.profondita === 1,
    );
    const madre = trovaCartella(alberoCartelle, cartellaCorrente);
    return appiattisciCartelle(madre?.figlie ?? []).filter((riga) => riga.profondita === 1);
  }, [alberoCartelle, cartellaCorrente]);

  /** Le copie di ciascun documento pubblicato: le porta la sola vista del redattore. */
  const indiceCopie = useMemo(
    () => copiePerIndirizzo(daPubblicare.dato ?? []),
    [daPubblicare.dato],
  );

  const ricaricaTutto = () => {
    cartelle.ricarica();
    contenuti.ricarica();
    daPubblicare.ricarica();
  };

  /**
   * Il commento pubblico dell'obbligo, finche' non ha contenuti pubblicati.
   *
   * In «Scadenze e allerte» lo si scrive dalla riga dell'obbligo scoperto, ma
   * quella riga sparisce appena si carica una bozza — e il commento resta sul
   * portale fino alla pubblicazione. Qui lo si ritrova finche' serve: dopo la
   * pubblicazione l'API lo ha gia' cancellato, e ne rifiuta uno nuovo.
   */
  const notaAssenza = contenuti.dato?.nodo.nota_assenza ?? null;
  const notaAssenzaIl = contenuti.dato?.nodo.nota_assenza_il ?? null;
  const conContenutiPubblicati = (contenuti.dato?.pubblicazioni ?? []).some(
    (pubblicazione) => pubblicazione.stato === "pubblicato",
  );

  const conErrore = async (azione: () => Promise<void>, ricaduta: string) => {
    setErroreOperazione(null);
    setEsito(null);
    setInLavorazione(true);
    try {
      await azione();
    } catch (causa) {
      setErroreOperazione(causa instanceof ErroreApi ? causa.messaggioEsteso : ricaduta);
    } finally {
      setInLavorazione(false);
    }
  };

  // ----------------------------------------------------------------- azioni -- //

  const durataScelta = leggiDurata(sceltaDurata, finoAl);
  const scadenzaScelta = leggiScadenzaFile(validoFinoAl);

  const carica = () =>
    conErrore(async () => {
      if (!idNodo || !fileScelto) return;
      const titolo = titoloDocumento.trim();
      if (!titolo) {
        setErroreOperazione(
          "Il titolo e' obbligatorio: e' il testo del link pubblico, e deve descrivere il contenuto.",
        );
        return;
      }
      if (durataScelta.errore) {
        setErroreOperazione(durataScelta.errore);
        return;
      }
      if (scadenzaScelta.errore) {
        setErroreOperazione(scadenzaScelta.errore);
        return;
      }
      const richiestaCopie = corpoCopie(copieScelte, percorsoDi);
      if (richiestaCopie.errore) {
        setErroreOperazione(richiestaCopie.errore);
        return;
      }
      const caricato = await caricaDocumentoSuNodo(idNodo, fileScelto, titolo, {
        cartellaId: cartellaCorrente,
        enteId,
        durataAnni: durataScelta.durata_anni,
        finoAl: durataScelta.fino_al,
        validoFinoAl: scadenzaScelta.valore,
        copie: richiestaCopie.copie,
      });
      setFileScelto(null);
      setTitoloDocumento("");
      setSceltaDurata("");
      setFinoAl("");
      setValidoFinoAl("");
      setCopieScelte([]);
      // Il giudizio di accessibilita' e' gia' nella risposta: si mostra ora,
      // con il file originale ancora sotto mano, e non quando lo si scopre
      // dopo averlo pubblicato.
      setEsito(
        <>
          Documento caricato in bozza: non e' ancora sul portale. Diventa pubblico con
          «Pubblica», nel riquadro «Da pubblicare».
          {caricato.copie_collegate?.length ? (
            <>
              {" "}
              Copiato anche in: {testoCopie(caricato.copie_collegate)}. «Pubblica in tutti gli
              obblighi» li mette online insieme.
            </>
          ) : null}
          <EsitoAccessibilita key={caricato.id} documento={caricato} />
        </>,
      );
      ricaricaTutto();
    }, "Non e' stato possibile caricare il documento.");

  /**
   * Transizione bozza → pubblicato, dalla pagina in cui si carica.
   *
   * Il perimetro lo verifica l'API: `pubblicazione_nel_perimetro` guarda ente e
   * nodi assegnati, non il ruolo, quindi un redattore pubblica dentro i propri
   * obblighi. Offrirlo solo al super admin — com'era — significava lasciare
   * senza il gesto conclusivo proprio chi carica i documenti.
   */
  const pubblicaOra = (pubblicazione: Pubblicazione) =>
    conErrore(async () => {
      setInPubblicazione(pubblicazione.id);
      try {
        const esito = await pubblica(pubblicazione.id);
        setEsito(
          `«${pubblicazione.titolo}» e' pubblicata: versione n. ${esito.versione.progressivo} del ${esito.versione.data_prima_pubblicazione}.` +
            (esito.at_map_rigenerata
              ? " La at_map e' stata rigenerata."
              : " La at_map non risulta rigenerata: verificare il pannello di conformita'."),
        );
        ricaricaTutto();
      } finally {
        setInPubblicazione(null);
      }
    }, "Non e' stato possibile pubblicare.");

  const archiviaOra = (documento: Documento) =>
    conErrore(async () => {
      await archiviaDocumento(documento.id);
      setDaArchiviare(null);
      setEsito(
        `«${documento.titolo}» e' passato nell'archivio dell'obbligo: non e' piu' fra i contenuti correnti, e resta accessibile al suo indirizzo.`,
      );
      ricaricaTutto();
    }, "Non e' stato possibile archiviare il documento.");

  const creaSottocartella = (titolo: string) =>
    conErrore(async () => {
      if (!idNodo) return;
      await creaCartella(idNodo, { titolo, id_padre: cartellaCorrente }, enteId);
      setNuovaCartella(null);
      ricaricaTutto();
    }, "Non e' stato possibile creare la cartella.");

  const spostaIn = (documento: Documento, destinazione: number | null) =>
    conErrore(async () => {
      await spostaDocumento(documento.id, destinazione);
      setDaSpostare(null);
      setEsito(
        `«${documento.titolo}» spostato. L'indirizzo pubblico del file non e' cambiato.`,
      );
      ricaricaTutto();
    }, "Non e' stato possibile spostare il documento.");

  const spostaCartellaIn = (cartella: Cartella, destinazione: number | null) =>
    conErrore(async () => {
      await aggiornaCartella(cartella.id, { id_padre: destinazione });
      ricaricaTutto();
    }, "Non e' stato possibile spostare la cartella.");

  const elimina = (cartella: Cartella, ricorsiva: boolean) =>
    conErrore(async () => {
      await eliminaCartella(cartella.id, ricorsiva);
      setCartellaDaEliminare(null);
      if (cartellaCorrente === cartella.id) setCartellaCorrente(cartella.id_padre);
      ricaricaTutto();
    }, "Non e' stato possibile eliminare la cartella.");

  // ------------------------------------------------------------ trascinamento -- //

  const alRilascioSuCartella = (evento: DragEvent<HTMLElement>, destinazione: number | null) => {
    evento.preventDefault();
    const grezzo = evento.dataTransfer.getData("application/x-documento");
    if (!grezzo) return;
    const idDocumento = Number(grezzo);
    const riga = righe.find(({ documento }) => documento.id === idDocumento);
    if (riga) void spostaIn(riga.documento, destinazione);
  };

  // ------------------------------------------------------------------ resa -- //

  if (alberaturaInCaricamento) return <InCaricamento etichetta="Caricamento dell'alberatura…" />;

  if (foglieDisponibili.length === 0) {
    return (
      <StatoVuoto
        titolo="Nessun obbligo assegnato"
        testo="Il perimetro assegnato non comprende alcun obbligo su cui caricare documenti. Si rivolga all'amministratore del sistema."
      />
    );
  }

  const briciole = percorsoCartella(alberoCartelle, cartellaCorrente);

  return (
    <div className="pila">
      <IntestazionePagina
        sopratitolo="Redazione"
        titolo="Documenti e cartelle"
        sottotitolo="Caricamento diretto sui singoli obblighi, con la ripartizione facoltativa in cartelle. Il file entra in bozza: diventa pubblico quando la bozza viene pubblicata dall'alberatura. Le cartelle non compaiono in alcun indirizzo pubblico, quindi spostare un file non cambia il link con cui e' gia' stato pubblicato."
      />

      {erroreOperazione ? (
        <Avviso tono="critico" titolo="Operazione non riuscita">
          {erroreOperazione}
        </Avviso>
      ) : null}
      {esito ? <Avviso tono="positivo">{esito}</Avviso> : null}

      <div className="colonne-documenti">
        <Scheda titolo="Alberatura" descrizione="Scegliere l'obbligo su cui lavorare." fitto>
          <AlberoNodi
            nodi={nodi}
            selezionato={idNodo}
            selezionabile={selezionabile}
            etichettaNonSelezionabile="fuori perimetro o non applicabile"
            onSeleziona={(nodo) => {
              setNodoScelto(nodo);
              // L'obbligo scelto sta nell'URL: la pagina si riapre sullo
              // stesso punto e l'assistente sa su quale nodo e' l'operatore.
              setRichiestaApplicata(nodo.id_nodo);
              setParametri({ nodo: nodo.id_nodo }, { replace: true });
              setCartellaCorrente(null);
              setErroreOperazione(null);
              setEsito(null);
              // Le copie scelte valevano per l'obbligo di prima: quello nuovo
              // potrebbe essere fra loro.
              setCopieScelte([]);
            }}
          />
        </Scheda>

        <div className="pila">
          {!nodoScelto ? (
            <Scheda>
              <StatoVuoto
                titolo="Nessun obbligo scelto"
                testo="Scegliere un obbligo nell'alberatura a sinistra per vederne documenti e cartelle."
              />
            </Scheda>
          ) : (
            <>
              <Scheda
                titolo={nodoScelto.denominazione}
                descrizione={nodoScelto.id_nodo}
                azioni={
                  <Bottone
                    variante="contorno"
                    dimensione="piccolo"
                    onClick={() => setNuovaCartella("")}
                    disabled={inLavorazione}
                  >
                    Nuova cartella
                  </Bottone>
                }
              >
                {contenuti.dato && !conContenutiPubblicati ? (
                  <div className="riga">
                    <span className="campo__aiuto">
                      {notaAssenza
                        ? `Commento sul portale${notaAssenzaIl ? ` dal ${notaAssenzaIl}` : ""}: «${notaAssenza}»`
                        : "Nessun contenuto pubblicato: si puo' spiegare sul portale perche'."}
                    </span>
                    <Bottone
                      variante="fantasma"
                      dimensione="piccolo"
                      disabled={inLavorazione}
                      onClick={() => setCommentoAperto(true)}
                    >
                      {notaAssenza ? "Modifica commento" : "Commento pubblico…"}
                    </Bottone>
                  </div>
                ) : null}

                {/* Briciole delle cartelle: la radice e' sempre raggiungibile,
                    anche da dentro un annidamento profondo. */}
                <nav className="briciole-cartelle" aria-label="Percorso nelle cartelle">
                  <button
                    type="button"
                    className="briciole-cartelle__voce"
                    aria-current={cartellaCorrente == null ? "true" : undefined}
                    onClick={() => setCartellaCorrente(null)}
                    onDragOver={(evento) => evento.preventDefault()}
                    onDrop={(evento) => alRilascioSuCartella(evento, null)}
                  >
                    Radice dell'obbligo
                  </button>
                  {briciole
                    ? briciole.split(" / ").map((voce) => (
                        <span key={voce} className="briciole-cartelle__separatore">
                          {" / "}
                          {voce}
                        </span>
                      ))
                    : null}
                </nav>

                {cartelle.inCaricamento || contenuti.inCaricamento ? (
                  <InCaricamento />
                ) : (
                  <>
                    <h3 className="scheda__titolo">Cartelle</h3>
                    {sottocartelle.length === 0 ? (
                      <p className="campo__aiuto">
                        Nessuna cartella qui. I documenti caricati restano in radice
                        dell'obbligo, che e' il caso piu' comune.
                      </p>
                    ) : (
                      <ul className="elenco-cartelle">
                        {sottocartelle.map(({ cartella }) => (
                          <li
                            key={cartella.id}
                            className="elenco-cartelle__voce"
                            onDragOver={(evento) => evento.preventDefault()}
                            onDrop={(evento) => alRilascioSuCartella(evento, cartella.id)}
                          >
                            <button
                              type="button"
                              className="elenco-cartelle__apri"
                              onClick={() => setCartellaCorrente(cartella.id)}
                            >
                              <span className="elenco-cartelle__titolo">{cartella.titolo}</span>
                              <span className="albero__meta">
                                {cartella.documenti_totali}{" "}
                                {cartella.documenti_totali === 1 ? "documento" : "documenti"}
                                {cartella.figlie.length
                                  ? ` · ${cartella.figlie.length} sottocartelle`
                                  : ""}
                              </span>
                            </button>
                            <div className="riga">
                              {cartella.e_annualita ? (
                                <Chip tono="primario" pallino={false}>
                                  annualita'
                                </Chip>
                              ) : null}
                              <Bottone
                                variante="fantasma"
                                dimensione="piccolo"
                                disabled={inLavorazione}
                                onClick={() => void spostaCartellaIn(cartella, null)}
                              >
                                Porta in radice
                              </Bottone>
                              <Bottone
                                variante="critico"
                                dimensione="piccolo"
                                disabled={inLavorazione}
                                onClick={() => setCartellaDaEliminare(cartella)}
                              >
                                Elimina
                              </Bottone>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}

                    <h3 className="scheda__titolo">Documenti</h3>
                    {/* Accanto ai chip e non altrove: chi legge «da correggere»
                        in tabella trova qui che cosa vuol dire e che cosa fare. */}
                    <LegendaAccessibilita />
                    {righe.length === 0 ? (
                      <p className="campo__aiuto">
                        Nessun documento pubblicato in questa posizione. Questo elenco e' la
                        vista pubblica dell'obbligo: i documenti in bozza compaiono qui dopo la
                        pubblicazione, come sul portale. Cio' che e' caricato e non ancora
                        pubblico sta nel riquadro «Da pubblicare», qui sotto.
                      </p>
                    ) : (
                      // `tabella-guscio` dà alla tabella il proprio scorrimento
                      // orizzontale: senza, a larghezze strette le colonne di
                      // destra escono dal riquadro e non si raggiungono.
                      <div className="tabella-guscio">
                      <table className="tabella">
                        <caption>
                          {righe.length} {righe.length === 1 ? "documento" : "documenti"} in
                          questa posizione, con formato e data di aggiornamento.
                        </caption>
                        <thead>
                          {/* Quattro colonne, non cinque: la pubblicazione sta
                              sotto il titolo perché è contesto, non un dato da
                              confrontare colonna per colonna. Una colonna in
                              meno è una colonna che non esce dal riquadro.

                              L'accessibilità sta per la stessa ragione **nella
                              colonna del formato** e non in una quinta: formato
                              e leggibilità sono due facce dello stesso fatto —
                              come si apre il file e se si riesce a leggerlo — e
                              chi guarda l'uno guarda anche l'altro. L'elenco
                              completo di ciò che va corretto, attraverso tutti
                              gli obblighi, sta in «Accessibilità dei
                              documenti». */}
                          <tr>
                            <th scope="col">Titolo</th>
                            <th scope="col">Formato e accessibilità</th>
                            <th scope="col">In pubblicazione</th>
                            <th scope="col">Azioni</th>
                          </tr>
                        </thead>
                        <tbody>
                          {righe.map(({ documento, pubblicazione, stato }) => (
                            <tr
                              key={documento.id}
                              draggable
                              onDragStart={(evento) =>
                                evento.dataTransfer.setData(
                                  "application/x-documento",
                                  String(documento.id),
                                )
                              }
                            >
                              <th scope="row">
                                <span className="documento-titolo">{documento.titolo}</span>
                                <span className="documento-contesto">
                                  <span className="albero__meta">{pubblicazione}</span>
                                  <ChipStato stato={stato} />
                                </span>
                                {copieDi(documento, indiceCopie).length > 0 ? (
                                  <span className="campo__aiuto">
                                    Pubblicato anche in: {testoCopie(copieDi(documento, indiceCopie))}
                                  </span>
                                ) : null}
                              </th>
                              <td>
                                <Chip tono="neutro" pallino={false}>
                                  {documento.formato.toUpperCase()}
                                </Chip>{" "}
                                <span className="testo-fioco">{documento.peso}</span>
                                {documento.accessibilita ? (
                                  <div>
                                    <ChipAccessibilita
                                      stato={documento.accessibilita}
                                    />
                                  </div>
                                ) : null}
                              </td>
                              <td>
                                {/* La durata accanto alle date: il Comune cercava
                                    proprio questo — da quando, fino a quando, e
                                    perche' quella durata. */}
                                <span className="dato">
                                  dal {documento.pubblicato_dal ?? documento.data_pubblicazione ?? "—"}
                                </span>
                                {documento.fine_pubblicazione ? (
                                  <div className="dato">fino al {documento.fine_pubblicazione}</div>
                                ) : null}
                                <div className="campo__aiuto">
                                  {testoDurata(
                                    durataRisolta(documento, { durata_anni: durataObbligo }),
                                  )}
                                </div>
                                {documento.valido_fino_al ? (
                                  <div className="campo__aiuto">
                                    da sostituire entro il {documento.valido_fino_al}
                                  </div>
                                ) : null}
                              </td>
                              <td>
                                <div className="riga">
                                  <Bottone
                                    variante="contorno"
                                    dimensione="piccolo"
                                    disabled={inLavorazione}
                                    onClick={() => setDaSpostare(documento)}
                                  >
                                    Sposta in…
                                  </Bottone>
                                  <Bottone
                                    variante="fantasma"
                                    dimensione="piccolo"
                                    disabled={inLavorazione}
                                    onClick={() => setDurataDi(documento)}
                                    aria-label={`Cambia la durata di «${documento.titolo}»`}
                                  >
                                    Durata…
                                  </Bottone>
                                  <Bottone
                                    variante="fantasma"
                                    dimensione="piccolo"
                                    disabled={inLavorazione}
                                    onClick={() => setScadenzaDi(documento)}
                                    aria-label={`Cambia la scadenza di «${documento.titolo}»`}
                                  >
                                    Scadenza…
                                  </Bottone>
                                  <Bottone
                                    variante="fantasma"
                                    dimensione="piccolo"
                                    disabled={inLavorazione}
                                    onClick={() => setDaArchiviare(documento)}
                                    aria-label={`Archivia «${documento.titolo}»`}
                                  >
                                    Archivia
                                  </Bottone>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    )}

                    {archiviati.length > 0 ? (
                      <details className="legenda-compatta">
                        <summary>
                          Archiviati ({archiviati.length}): usciti dalla pubblicazione corrente,
                          restano accessibili
                        </summary>
                        <ul className="pila-stretta">
                          {archiviati.map(({ documento, pubblicazione }) => (
                            <li key={documento.id}>
                              <span className="documento-titolo">{documento.titolo}</span>
                              <span className="campo__aiuto">
                                {pubblicazione} · {documento.formato.toUpperCase()}{" "}
                                {documento.peso}
                                {documento.archiviato_il
                                  ? ` · archiviato il ${documento.archiviato_il}`
                                  : " · pubblicazione archiviata"}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    ) : null}
                  </>
                )}
              </Scheda>

              {/* Da pubblicare: bozze e revisioni dell'obbligo scelto.
                  E' lo stesso blocco dell'alberatura — stesso dato, stesso
                  comando, stessa resa — e sta qui perche' chi carica un file
                  deve poterlo pubblicare senza cambiare pagina, tanto piu' che
                  la pagina dell'alberatura e' riservata al super admin mentre
                  l'API consente al redattore di pubblicare nel suo perimetro. */}
              <Scheda
                titolo="Da pubblicare"
                descrizione="Documenti caricati e non ancora pubblici. Diventano visibili sul portale solo con «Pubblica»."
                fitto
              >
                {daPubblicare.errore ? (
                  <div style={{ padding: "1.25rem" }}>
                    <Avviso tono="critico" titolo="Elenco non disponibile">
                      {daPubblicare.errore.messaggioEsteso}
                    </Avviso>
                  </div>
                ) : daPubblicare.inCaricamento ? (
                  <InCaricamento />
                ) : inAttesa.length === 0 ? (
                  <p className="campo__aiuto" style={{ padding: "1.25rem" }}>
                    Nulla in attesa: su questo obbligo tutto cio' che e' stato caricato e' anche
                    pubblicato.
                  </p>
                ) : (
                  <ContenutiNodo
                    pubblicazioni={inAttesa}
                    percorso={percorsoNodo(catene, nodoScelto.id_nodo)}
                    inPubblicazione={inPubblicazione}
                    onPubblica={(pubblicazione) => void pubblicaOra(pubblicazione)}
                    onPubblicaInTutti={(pubblicazione) =>
                      setDaPubblicareTutti(
                        daPubblicareInTutti(pubblicazione, nodoScelto.denominazione),
                      )
                    }
                  />
                )}
              </Scheda>

              <Scheda
                titolo="Carica un documento"
                descrizione={
                  cartellaCorrente == null
                    ? "Il file finira' in radice dell'obbligo."
                    : `Il file finira' nella cartella «${briciole}».`
                }
              >
                <div className="pila-stretta">
                  <ZonaRilascio onFile={setFileScelto} disabilitata={inLavorazione} />
                  {fileScelto ? (
                    <>
                      <FileScelto file={fileScelto} onRimuovi={() => setFileScelto(null)} />
                      <CampoLibero
                        etichetta="Titolo del link pubblico"
                        valore={titoloDocumento}
                        onCambia={setTitoloDocumento}
                        aiuto="E' il testo che legge chi naviga con uno screen reader: deve descrivere il contenuto, non il file."
                      />
                      {regole.errore ? (
                        <Avviso tono="attenzione" titolo="Regole degli obblighi non disponibili">
                          {regole.errore.messaggioEsteso} «Come da regola dell'obbligo» resta la
                          scelta giusta: la durata la applica il sistema, qui non si puo' mostrare.{" "}
                          <Bottone variante="contorno" dimensione="piccolo" onClick={regole.ricarica}>
                            Riprova
                          </Bottone>
                        </Avviso>
                      ) : null}
                      <CampoDurata
                        scelta={sceltaDurata}
                        finoAl={finoAl}
                        massimoAnni={DURATA_MASSIMA_AL_CARICAMENTO}
                        durataObbligo={durataObbligo}
                        regoleNote={regoleNote}
                        onCambia={(scelta, data) => {
                          setSceltaDurata(scelta);
                          setFinoAl(data);
                        }}
                      />
                      <CampoDato
                        etichetta="Scadenza del file (facoltativa)"
                        tipo="data"
                        valore={validoFinoAl}
                        onCambia={setValidoFinoAl}
                        errore={validoFinoAl.trim() ? scadenzaScelta.errore : null}
                        aiuto="Entro questa data va caricato un file aggiornato. Passata, il file resta pubblicato e compare fra le allerte, da sostituire."
                      />
                      {candidatiCopie.length > 0 ? (
                        <SceltaObblighi
                          candidati={candidatiCopie}
                          scelte={copieScelte}
                          onCambia={setCopieScelte}
                          percorsoDi={percorsoDi}
                          durataObbligoDi={(id) => regolaDi.get(id)?.durata_anni}
                          regoleNote={regoleNote}
                        />
                      ) : null}
                      <Bottone
                        variante="primario"
                        inCorso={inLavorazione}
                        onClick={() => void carica()}
                      >
                        Carica in bozza
                      </Bottone>
                    </>
                  ) : null}
                </div>
              </Scheda>
            </>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------ modali -- */}

      <Modale
        titolo="Nuova cartella"
        aperta={nuovaCartella !== null}
        onChiudi={() => setNuovaCartella(null)}
        pedice={
          <>
            <Bottone variante="fantasma" onClick={() => setNuovaCartella(null)}>
              Annulla
            </Bottone>
            <Bottone
              variante="primario"
              inCorso={inLavorazione}
              disabled={!nuovaCartella?.trim()}
              onClick={() => void creaSottocartella((nuovaCartella ?? "").trim())}
            >
              Crea
            </Bottone>
          </>
        }
      >
        <CampoLibero
          etichetta="Titolo della cartella"
          valore={nuovaCartella ?? ""}
          onCambia={setNuovaCartella}
          aiuto="Un anno («2026») o un titolo libero («Allegati»). Gli anni sono ordinati dal piu' recente."
        />
        {cartellaCorrente != null ? (
          <p className="campo__aiuto">Sara' creata dentro «{briciole}».</p>
        ) : null}
      </Modale>

      <Modale
        titolo={daSpostare ? `Sposta «${daSpostare.titolo}»` : "Sposta"}
        aperta={daSpostare !== null}
        onChiudi={() => setDaSpostare(null)}
        pedice={
          <Bottone variante="fantasma" onClick={() => setDaSpostare(null)}>
            Annulla
          </Bottone>
        }
      >
        <p>
          Ora si trova in{" "}
          <strong>
            {daSpostare?.cartella_id != null
              ? `«${percorsoCartella(alberoCartelle, daSpostare.cartella_id)}»`
              : "radice dell'obbligo"}
          </strong>
          .
        </p>
        <p className="campo__aiuto">
          Lo spostamento non cambia il nome del file, l'indirizzo con cui e' stato
          pubblicato ne' l'alias «-latest»: cambia solo sotto quale intestazione il
          documento compare nella pagina pubblica.
        </p>
        {alberoCartelle.length === 0 ? (
          <p className="campo__aiuto">
            Su questo obbligo non ci sono ancora cartelle: si creano con «Nuova cartella».
          </p>
        ) : null}
        <ul className="elenco-cartelle">
          <li className="elenco-cartelle__voce">
            <button
              type="button"
              className="elenco-cartelle__apri"
              // Come la cartella in cui il documento gia' sta: una destinazione
              // che non sposta nulla non va offerta.
              disabled={inLavorazione || (daSpostare?.cartella_id ?? null) === null}
              onClick={() => daSpostare && void spostaIn(daSpostare, null)}
            >
              Radice dell'obbligo
            </button>
          </li>
          {destinazioniAmmesse(alberoCartelle, null).map(({ cartella, profondita }) => (
            <li key={cartella.id} className="elenco-cartelle__voce">
              <button
                type="button"
                className="elenco-cartelle__apri"
                style={{ paddingLeft: `${profondita * 1.25}rem` }}
                disabled={inLavorazione || (daSpostare?.cartella_id ?? null) === cartella.id}
                onClick={() => daSpostare && void spostaIn(daSpostare, cartella.id)}
              >
                {cartella.titolo}
              </button>
            </li>
          ))}
        </ul>
      </Modale>

      {daPubblicareTutti ? (
        <ModalePubblicaInTutti
          voci={daPubblicareTutti}
          percorsoDi={percorsoDi}
          onChiudi={() => setDaPubblicareTutti(null)}
          onCompletato={ricaricaTutto}
        />
      ) : null}

      {commentoAperto && nodoScelto ? (
        <ModaleNotaAssenza
          key={nodoScelto.id_nodo}
          idNodo={nodoScelto.id_nodo}
          denominazione={nodoScelto.denominazione}
          notaAttuale={notaAssenza ?? undefined}
          enteId={enteId}
          onChiudi={() => setCommentoAperto(false)}
          onCompletato={(nodo) => {
            setCommentoAperto(false);
            setEsito(
              nodo.nota_assenza
                ? `Commento su «${nodo.denominazione}» pubblicato sul portale: resta finche' l'obbligo non avra' un contenuto pubblicato.`
                : `Commento su «${nodo.denominazione}» tolto dal portale.`,
            );
            contenuti.ricarica();
            // Le righe degli obblighi scoperti leggono la nota dall'alberatura.
            ricaricaAlberatura();
          }}
        />
      ) : null}

      {scadenzaDi ? (
        <ModaleScadenza
          key={scadenzaDi.id}
          documento={scadenzaDi}
          onChiudi={() => setScadenzaDi(null)}
          onCompletato={(aggiornato) => {
            setScadenzaDi(null);
            setEsito(
              aggiornato.valido_fino_al
                ? `«${aggiornato.titolo}» va sostituito entro il ${aggiornato.valido_fino_al}.`
                : `Scadenza di «${aggiornato.titolo}» tolta.`,
            );
            ricaricaTutto();
          }}
        />
      ) : null}

      {durataDi ? (
        <ModaleDurata
          key={durataDi.id}
          documento={durataDi}
          durataObbligo={durataObbligo}
          regoleNote={regoleNote}
          onChiudi={() => setDurataDi(null)}
          onCompletato={(aggiornato) => {
            setDurataDi(null);
            setEsito(
              aggiornato.fine_pubblicazione
                ? `«${aggiornato.titolo}» resta in pubblicazione fino al ${aggiornato.fine_pubblicazione}. L'indirizzo del file non e' cambiato.`
                : `Durata di «${aggiornato.titolo}» aggiornata.`,
            );
            ricaricaTutto();
          }}
        />
      ) : null}

      <Modale
        titolo={daArchiviare ? `Archivia «${daArchiviare.titolo}»` : "Archivia"}
        aperta={daArchiviare !== null}
        onChiudi={() => setDaArchiviare(null)}
        pedice={
          <>
            <Bottone variante="fantasma" onClick={() => setDaArchiviare(null)}>
              Annulla
            </Bottone>
            <Bottone
              variante="primario"
              inCorso={inLavorazione}
              onClick={() => daArchiviare && void archiviaOra(daArchiviare)}
            >
              Archivia il documento
            </Bottone>
          </>
        }
      >
        <p>
          Il documento esce dalla pubblicazione corrente e passa nell'archivio della pagina
          dell'obbligo. <strong>Non viene cancellato</strong>: resta accessibile al suo indirizzo, e
          chi consulta l'archivio lo trova.
        </p>
        <p className="campo__aiuto">
          E' cio' che accade da solo alla fine della durata di pubblicazione
          {daArchiviare?.fine_pubblicazione ? ` (per questo documento, dopo il ${daArchiviare.fine_pubblicazione})` : ""}
          ; qui lo si anticipa, per un documento che il Comune ritira prima. Non si torna indietro:
          per rimetterlo online si carica di nuovo.
        </p>
      </Modale>

      <Modale
        titolo={cartellaDaEliminare ? `Elimina «${cartellaDaEliminare.titolo}»` : "Elimina"}
        aperta={cartellaDaEliminare !== null}
        onChiudi={() => setCartellaDaEliminare(null)}
        pedice={
          <>
            <Bottone variante="fantasma" onClick={() => setCartellaDaEliminare(null)}>
              Annulla
            </Bottone>
            <Bottone
              variante="critico"
              inCorso={inLavorazione}
              onClick={() => cartellaDaEliminare && void elimina(cartellaDaEliminare, true)}
            >
              Elimina la cartella
            </Bottone>
          </>
        }
      >
        <p>
          I documenti <strong>non</strong> vengono cancellati: tornano in radice
          dell'obbligo e restano raggiungibili ai loro indirizzi. Un contenuto
          pubblicato deve restare accessibile per tutta la durata di pubblicazione
          (art. 8, comma 3, d.lgs. 33/2013), e un gesto di sola organizzazione non
          puo' farlo sparire.
        </p>
        {cartellaDaEliminare ? (
          <p className="campo__aiuto">
            Contiene {cartellaDaEliminare.documenti_totali} documenti e{" "}
            {cartellaDaEliminare.figlie.length} sottocartelle.
          </p>
        ) : null}
      </Modale>
    </div>
  );
}
